import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Storage as StorageIcon,
  Timeline as TimelineIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import timezoneService from '../services/timezoneService';

interface SystemStats {
  users: {
    total: number;
    active: number;
    byRole: Record<string, number>;
  };
  workspaces: {
    total: number;
  };
  screenshots: {
    total: number;
  };
  timeEntries: {
    total: number;
  };
  activity: {
    last24Hours: {
      screenshots: number;
      timeEntries: number;
    };
  };
}

interface DatabaseHealth {
  tables: Array<{
    tableName: string;
    rowCount: number;
  }>;
  databaseType: string;
  healthStatus: string;
}

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  workspaceId: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

interface Workspace {
  id: string;
  name: string;
  slug: string;
  plan: string;
  memberCount: number;
  createdAt: string;
}

interface ActivityLog {
  id: string;
  type: string;
  userId: string;
  userName: string;
  userEmail: string;
  createdAt: string;
}

export const SuperAdminDashboard: React.FC = () => {
  const { state: authState } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [dbHealth, setDbHealth] = useState<DatabaseHealth | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activity, setActivity] = useState<ActivityLog[]>([]);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null);

  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🧠 SuperAdmin: Loading all data...');
      
      const [statsData, dbData, usersData, workspacesData, activityData] = await Promise.all([
        apiService.getSystemStats(),
        apiService.getDatabaseHealth(),
        apiService.getAdminUsers(),
        apiService.getAdminWorkspaces(),
        apiService.getActivityLogs(20),
      ]);

      console.log('🧠 SuperAdmin: Data loaded:', { statsData, dbData, usersData, workspacesData, activityData });

      setStats(statsData);
      setDbHealth(dbData);
      setUsers(usersData);
      setWorkspaces(workspacesData);
      setActivity(activityData);
    } catch (err) {
      console.error('🧠 SuperAdmin: Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load system data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const handleDeleteUserClick = (userId: string, userName: string) => {
    if (userId === authState.user?.id) {
      setError('You cannot delete your own account!');
      return;
    }
    setUserToDelete({ id: userId, name: userName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      console.log('🗑️ SuperAdmin: Deleting user:', userToDelete);
      await apiService.adminDeleteUser(userToDelete.id);
      
      setSuccess(`User ${userToDelete.name} deleted successfully`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      
      // Reload data
      await loadAllData();
    } catch (err) {
      console.error('🗑️ SuperAdmin: Delete error:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete user');
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!stats || !dbHealth) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load system data</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DashboardIcon />
          Superadmin System Dashboard
        </Typography>
        <IconButton onClick={loadAllData} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* System Overview Cards */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon />
            System Overview
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3">{stats.users.total}</Typography>
                <Typography variant="body2">Total Users</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3">{stats.users.active}</Typography>
                <Typography variant="body2">Active Users</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3">{stats.workspaces.total}</Typography>
                <Typography variant="body2">Workspaces</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3">{stats.screenshots.total}</Typography>
                <Typography variant="body2">Screenshots</Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Time Entries: {stats.timeEntries.total} | 
              Last 24h Activity: {stats.activity.last24Hours.screenshots} screenshots, {stats.activity.last24Hours.timeEntries} time entries
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Database Health */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorageIcon />
            Database Health
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Chip 
              icon={dbHealth.healthStatus === 'healthy' ? <CheckCircleIcon /> : <ErrorIcon />}
              label={`Status: ${dbHealth.healthStatus.toUpperCase()}`}
              color={dbHealth.healthStatus === 'healthy' ? 'success' : 'error'}
            />
            <Chip label={`Type: ${dbHealth.databaseType}`} variant="outlined" />
            <Chip label={`Tables: ${dbHealth.tables.length}`} variant="outlined" />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Table Name</TableCell>
                  <TableCell align="right">Row Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dbHealth.tables.map((table) => (
                  <TableRow key={table.tableName}>
                    <TableCell>{table.tableName}</TableCell>
                    <TableCell align="right">{table.rowCount.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Activity Monitor */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon />
            Recent Activity
          </Typography>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {activity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No recent activity</Typography>
            ) : (
              activity.map((log) => (
                <Box key={log.id} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                  <Typography variant="body2">
                    <Chip 
                      label={log.type}
                      size="small"
                      color={log.type === 'screenshot' ? 'primary' : 'secondary'}
                      sx={{ mr: 1 }}
                    />
                    <strong>{log.userName}</strong> ({log.userEmail})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(log.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Workspaces */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon />
            Workspaces ({workspaces.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell align="right">Members</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workspaces.map((workspace) => (
                  <TableRow key={workspace.id}>
                    <TableCell>{workspace.name}</TableCell>
                    <TableCell>{workspace.slug}</TableCell>
                    {/* Removed duplicate TableCell */}
                    <TableCell align="right">{workspace.memberCount}</TableCell>
                    <TableCell>
                      {new Date(workspace.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* All Users */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon />
            All Users ({users.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Workspace</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role}
                        size="small"
                        color={
                          user.role === 'owner' ? 'error' :
                          user.role === 'admin' || user.role === 'administrator' ? 'warning' :
                          'default'
                        }
                      />
                    </TableCell>
                    <TableCell>{user.workspaceId}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={user.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteUserClick(user.id, user.name)}
                        disabled={user.id === authState.user?.id}
                        title={user.id === authState.user?.id ? 'Cannot delete yourself' : 'Delete user'}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{userToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone. All user data will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
